
"use client";

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useTheme as useNextTheme } from 'next-themes';
import type { ColorScheme, ColorModeValues } from '@/types';

// Define the 5 color schemes
const colorSchemes: ColorScheme[] = [
  {
    name: 'default',
    displayName: 'Default Indigo',
    light: {
      primary: '231 48% 48%', primaryForeground: '0 0% 98%',
      background: '206 17% 94%', foreground: '240 10% 3.9%',
      card: '0 0% 100%', cardForeground: '240 10% 3.9%',
      popover: '0 0% 100%', popoverForeground: '240 10% 3.9%',
      secondary: '240 4.8% 95.9%', secondaryForeground: '240 5.9% 10%',
      muted: '240 4.8% 95.9%', mutedForeground: '240 3.8% 46.1%',
      accent: '36 100% 50%', accentForeground: '20 14.3% 4.1%',
      destructive: '0 84.2% 60.2%', destructiveForeground: '0 0% 98%',
      border: '240 5.9% 90%', input: '240 5.9% 90%', ring: '231 48% 48%',
    },
    dark: {
      primary: '231 48% 48%', primaryForeground: '0 0% 98%',
      background: '240 10% 3.9%', foreground: '0 0% 98%',
      card: '240 5.9% 10%', cardForeground: '0 0% 98%',
      popover: '240 10% 3.9%', popoverForeground: '0 0% 98%',
      secondary: '240 3.7% 15.9%', secondaryForeground: '0 0% 98%',
      muted: '240 3.7% 15.9%', mutedForeground: '240 5% 64.9%',
      accent: '36 100% 50%', accentForeground: '20 14.3% 4.1%',
      destructive: '0 62.8% 30.6%', destructiveForeground: '0 0% 98%',
      border: '240 3.7% 15.9%', input: '240 3.7% 15.9%', ring: '231 48% 48%',
    },
  },
  {
    name: 'forest',
    displayName: 'Forest Green',
    light: { // Shades of green, brown, beige
      primary: '120 39% 41%', primaryForeground: '0 0% 98%', // Dark Green
      background: '46 45% 90%', foreground: '45 25% 15%',   // Light Beige
      card: '45 35% 85%', cardForeground: '45 25% 15%',      // Slightly darker Beige
      popover: '45 35% 85%', popoverForeground: '45 25% 15%',
      secondary: '45 20% 75%', secondaryForeground: '45 25% 15%', // Lighter brown
      muted: '45 20% 80%', mutedForeground: '45 15% 40%',
      accent: '56 62% 50%', accentForeground: '0 0% 100%',    // Yellow-Green (Lime)
      destructive: '0 72% 51%', destructiveForeground: '0 0% 98%', // Standard Red
      border: '45 20% 70%', input: '45 20% 70%', ring: '120 39% 41%',
    },
    dark: { // Darker greens, deep browns
      primary: '120 39% 41%', primaryForeground: '0 0% 98%', // Dark Green (same as light for consistency)
      background: '120 25% 10%', foreground: '46 45% 90%',   // Very Dark Green-Brown
      card: '120 20% 15%', cardForeground: '46 45% 90%',      // Darker Green-Brown
      popover: '120 20% 15%', popoverForeground: '46 45% 90%',
      secondary: '120 15% 25%', secondaryForeground: '46 45% 90%', // Muted Dark Green
      muted: '120 15% 20%', mutedForeground: '46 30% 70%',
      accent: '56 62% 50%', accentForeground: '0 0% 0%',    // Yellow-Green (Lime)
      destructive: '0 60% 40%', destructiveForeground: '0 0% 98%', // Darker Red
      border: '120 15% 25%', input: '120 15% 25%', ring: '120 39% 41%',
    },
  },
  {
    name: 'ocean',
    displayName: 'Ocean Teal',
    light: { // Teal, light blue, sand
      primary: '180 50% 45%', primaryForeground: '0 0% 98%', // Teal
      background: '195 53% 92%', foreground: '195 30% 20%',  // Light Sky Blue
      card: '195 45% 85%', cardForeground: '195 30% 20%',
      popover: '195 45% 85%', popoverForeground: '195 30% 20%',
      secondary: '190 35% 78%', secondaryForeground: '195 30% 20%', // Lighter Teal
      muted: '190 35% 82%', mutedForeground: '190 25% 45%',
      accent: '25 80% 60%', accentForeground: '0 0% 0%',     // Coral/Orange
      destructive: '0 72% 51%', destructiveForeground: '0 0% 98%',
      border: '190 35% 75%', input: '190 35% 75%', ring: '180 50% 45%',
    },
    dark: { // Deep blue, dark teal
      primary: '180 50% 45%', primaryForeground: '0 0% 98%', // Teal
      background: '200 60% 8%', foreground: '195 53% 92%',    // Deep Ocean Blue
      card: '200 50% 12%', cardForeground: '195 53% 92%',
      popover: '200 50% 12%', popoverForeground: '195 53% 92%',
      secondary: '190 40% 20%', secondaryForeground: '195 53% 92%', // Dark Muted Teal
      muted: '190 40% 15%', mutedForeground: '190 30% 70%',
      accent: '25 80% 60%', accentForeground: '0 0% 0%',     // Coral/Orange
      destructive: '0 60% 40%', destructiveForeground: '0 0% 98%',
      border: '190 40% 20%', input: '190 40% 20%', ring: '180 50% 45%',
    },
  },
  {
    name: 'sunset',
    displayName: 'Sunset Orange',
    light: { // Oranges, reds, warm grays
      primary: '25 85% 55%', primaryForeground: '0 0% 98%', // Deep Orange
      background: '30 20% 92%', foreground: '30 30% 15%',   // Warm Light Gray
      card: '30 15% 88%', cardForeground: '30 30% 15%',
      popover: '30 15% 88%', popoverForeground: '30 30% 15%',
      secondary: '15 60% 80%', secondaryForeground: '30 30% 15%', // Light Peach
      muted: '15 50% 85%', mutedForeground: '15 25% 45%',
      accent: '330 70% 60%', accentForeground: '0 0% 100%',   // Magenta/Pink
      destructive: '0 72% 51%', destructiveForeground: '0 0% 98%',
      border: '15 40% 80%', input: '15 40% 80%', ring: '25 85% 55%',
    },
    dark: { // Deep reds, dark purples
      primary: '25 85% 55%', primaryForeground: '0 0% 98%', // Deep Orange
      background: '280 30% 10%', foreground: '30 20% 92%',  // Dark Purple
      card: '280 25% 15%', cardForeground: '30 20% 92%',
      popover: '280 25% 15%', popoverForeground: '30 20% 92%',
      secondary: '0 50% 25%', secondaryForeground: '30 20% 92%',   // Dark Red-Brown
      muted: '0 40% 20%', mutedForeground: '0 15% 70%',
      accent: '330 70% 60%', accentForeground: '0 0% 0%',   // Magenta/Pink
      destructive: '0 60% 40%', destructiveForeground: '0 0% 98%',
      border: '0 40% 25%', input: '0 40% 25%', ring: '25 85% 55%',
    },
  },
  {
    name: 'monochrome',
    displayName: 'Monochrome Gray',
    light: { // Grayscale
      primary: '240 5% 50%', primaryForeground: '0 0% 98%',  // Mid Gray
      background: '0 0% 96%', foreground: '0 0% 10%',      // Very Light Gray (almost white)
      card: '0 0% 100%', cardForeground: '0 0% 10%',
      popover: '0 0% 100%', popoverForeground: '0 0% 10%',
      secondary: '240 5% 90%', secondaryForeground: '0 0% 10%', // Lighter Gray
      muted: '240 5% 93%', mutedForeground: '240 3% 40%',
      accent: '240 5% 65%', accentForeground: '0 0% 98%',   // Slightly darker Mid Gray
      destructive: '0 72% 51%', destructiveForeground: '0 0% 98%', // Standard Red for contrast
      border: '240 5% 85%', input: '240 5% 85%', ring: '240 5% 50%',
    },
    dark: { // Dark Grayscale
      primary: '240 5% 60%', primaryForeground: '0 0% 98%',  // Mid Gray (slightly lighter for dark)
      background: '240 6% 10%', foreground: '0 0% 90%',      // Very Dark Gray (almost black)
      card: '240 5% 13%', cardForeground: '0 0% 90%',
      popover: '240 5% 13%', popoverForeground: '0 0% 90%',
      secondary: '240 4% 20%', secondaryForeground: '0 0% 90%', // Darker Gray
      muted: '240 4% 16%', mutedForeground: '240 2% 55%',
      accent: '240 5% 75%', accentForeground: '0 0% 0%',   // Lighter Mid Gray
      destructive: '0 60% 40%', destructiveForeground: '0 0% 98%', // Darker Red
      border: '240 4% 20%', input: '240 4% 20%', ring: '240 5% 60%',
    },
  },
];


interface ThemeCustomizationContextType {
  colorSchemes: ColorScheme[];
  selectedSchemeName: string;
  setSelectedSchemeName: (name: string) => void;
  applyColorScheme: (scheme: ColorScheme, mode: 'light' | 'dark') => void;
}

const ThemeCustomizationContext = createContext<ThemeCustomizationContextType | undefined>(undefined);

export function ThemeCustomizationProvider({ children }: { children: ReactNode }) {
  const { resolvedTheme } = useNextTheme(); // 'light', 'dark', or 'system' resolved to light/dark
  const [selectedSchemeName, setSelectedSchemeNameState] = useState<string>('default');
  const [isMounted, setIsMounted] = useState(false);

  const applyColorScheme = useCallback((scheme: ColorScheme, mode: 'light' | 'dark') => {
    const root = document.documentElement;
    const colorsToApply = scheme[mode];

    Object.entries(colorsToApply).forEach(([key, value]) => {
      // Convert camelCase key to kebab-case for CSS variable
      const cssVarName = `--${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
      root.style.setProperty(cssVarName, value);
    });

    // Sidebar variables need special handling if they are differently named or structured in your themes
    // For now, assuming sidebar uses the same vars like --primary, --background etc.
    // If your globals.css has specific --sidebar-primary etc., you'd map them here.
    // Example:
    // root.style.setProperty('--sidebar-background', colorsToApply.card); // If sidebar uses card color
    // root.style.setProperty('--sidebar-primary', colorsToApply.primary);
  }, []);
  
  useEffect(() => {
    setIsMounted(true);
    const storedSchemeName = localStorage.getItem('colorScheme');
    if (storedSchemeName && colorSchemes.find(s => s.name === storedSchemeName)) {
      setSelectedSchemeNameState(storedSchemeName);
    }
  }, []);


  useEffect(() => {
    if (!isMounted || !resolvedTheme) return;

    const currentScheme = colorSchemes.find(s => s.name === selectedSchemeName) || colorSchemes[0];
    const mode = resolvedTheme === 'dark' ? 'dark' : 'light';
    applyColorScheme(currentScheme, mode);

  }, [selectedSchemeName, resolvedTheme, applyColorScheme, isMounted]);

  const setSelectedSchemeName = (name: string) => {
    setSelectedSchemeNameState(name);
    localStorage.setItem('colorScheme', name);
  };


  return (
    <ThemeCustomizationContext.Provider value={{ colorSchemes, selectedSchemeName, setSelectedSchemeName, applyColorScheme }}>
      {children}
    </ThemeCustomizationContext.Provider>
  );
}

export function useThemeCustomization() {
  const context = useContext(ThemeCustomizationContext);
  if (context === undefined) {
    throw new Error('useThemeCustomization must be used within a ThemeCustomizationProvider');
  }
  return context;
}
